import { useState, useEffect, useCallback, useRef } from "react";
import { communicationAPI, socketService } from "../services/communicationAPI";
import {
  getMessages as getChatMessages,
  sendMessage as sendChatMessage,
} from "../services/AdminPortal/chatAPI";

export const useCommunication = () => {
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Map());
  const [unreadCounts, setUnreadCounts] = useState(new Map());

  // Debug unread counts changes
  useEffect(() => {
    console.log('📊 Unread counts updated:', unreadCounts);
  }, [unreadCounts]);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Get current user info from multiple sources
  const getCurrentUser = () => {
    // Try from localStorage first
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        return JSON.parse(storedUser);
      }
    } catch (e) {
      console.error("Error parsing user from localStorage:", e);
    }

    // Try to get from token
    try {
      const token =
        localStorage.getItem("adminToken") ||
        localStorage.getItem("staffToken") ||
        localStorage.getItem("customerToken") ||
        localStorage.getItem("bankingPartnerToken") ||
        localStorage.getItem("affiliatePartnerToken") ||
        localStorage.getItem("creditManagerToken");

      if (token) {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(window.atob(base64));
        return {
          _id: payload.userId || payload.id,
          name: payload.name || "User",
          userType: payload.userType || payload.role || "staff",
        };
      }
    } catch (e) {
      console.error("Error extracting user from token:", e);
    }

    // Return minimal user object if all else fails
    return { _id: "unknown", name: "User", userType: "staff" };
  };

  // Initialize socket connection
  useEffect(() => {
    // Get token from localStorage
    const getToken = () => {
      return (
        localStorage.getItem("adminToken") ||
        localStorage.getItem("staffToken") ||
        localStorage.getItem("customerToken") ||
        localStorage.getItem("bankingPartnerToken") ||
        localStorage.getItem("affiliatePartnerToken") ||
        localStorage.getItem("creditManagerToken") ||
        localStorage.getItem("token")
      );
    };

    const token = getToken();
    console.log("Socket connection token:", token ? "Token found" : "No token");

    if (token) {
      const connectSocket = async () => {
        try {
          await socketService.connect(token);
          setIsConnected(true);
          console.log("Socket connected successfully");
        } catch (error) {
          console.error("Failed to connect to socket:", error);
          setIsConnected(false);
          // Retry connection after 5 seconds
          setTimeout(() => {
            console.log("Retrying socket connection...");
            connectSocket();
          }, 5000);
        }
      };

      connectSocket();

      // Set up connection status monitoring
      const checkConnection = setInterval(() => {
        if (!socketService.isConnected && token) {
          console.log("Socket disconnected, attempting to reconnect...");
          connectSocket();
        }
      }, 10000); // Check every 10 seconds

      return () => {
        clearInterval(checkConnection);
        try {
          socketService.disconnect();
        } catch (err) {
          console.error("Socket disconnect error:", err);
        }
      };
    }
  }, []);

  // Socket event listeners
  useEffect(() => {
    if (!isConnected) return;

    try {
      // Fetch online users when socket connects
      fetchOnlineUsers();
      
      // Set up periodic refresh of online users
      const onlineUsersInterval = setInterval(() => {
        fetchOnlineUsers();
      }, 30000); // Refresh every 30 seconds

      const handleNewMessage = (data) => {
        try {
          console.log("Received new message:", data);
          const { message, conversationId } = data;

          if (!message || !conversationId) {
            console.error("Invalid message data received");
            return;
          }

          // Update messages if it's the current conversation
          if (
            currentConversation &&
            (currentConversation.conversationId === conversationId ||
              currentConversation._id === conversationId)
          ) {
            setMessages((prev) => {
              // Check if message already exists to avoid duplicates
              const exists = prev.some((msg) => msg._id === message._id);
              if (exists) {
                console.log('Message already exists, skipping duplicate');
                return prev;
              }

              // Ensure message has proper content structure
              const processedMessage = {
                ...message,
                content:
                  typeof message.content === "string"
                    ? message.content
                    : message.content?.text ||
                      message.text ||
                      message.message ||
                      "",
                createdAt: message.createdAt || new Date().toISOString(),
              };

              const newMessages = [...prev, processedMessage];

              // Save to localStorage
              try {
                const storageKey = `messages_${conversationId}`;
                localStorage.setItem(storageKey, JSON.stringify(newMessages));
              } catch (e) {
                console.error("Error saving message to localStorage:", e);
              }

              return newMessages;
            });
          }

          // Update unread count if not viewing this conversation
          const currentUser = getCurrentUser();
          const senderId = message.sender?.userId || message.senderId;
          const isOwnMessage = senderId === currentUser._id || senderId === currentUser.id;
          
          console.log('📨 Processing message:', {
            messageId: message._id,
            senderId,
            currentUserId: currentUser._id,
            isOwnMessage,
            conversationId,
            currentConversationId: currentConversation?.conversationId || currentConversation?._id
          });
          
          if (!isOwnMessage && (!currentConversation || 
              (currentConversation.conversationId !== conversationId && 
               currentConversation._id !== conversationId))) {
            setUnreadCounts((prevCounts) => {
              const currentCount = prevCounts.get(conversationId) || 0;
              const newCount = currentCount + 1;
              const newMap = new Map(prevCounts);
              newMap.set(conversationId, newCount);
              console.log(`📊 Updated unread count for ${conversationId}: ${newCount}`);
              return newMap;
            });
          }

          // Ensure conversation exists in the list
          setConversations((prev) => {
            const conversationExists = prev.some((conv) => 
              conv.conversationId === conversationId || conv._id === conversationId
            );
            
            if (!conversationExists) {
              console.log('🔄 Fetching conversations due to new message in unknown conversation');
              // Trigger a refresh of conversations
              setTimeout(() => {
                fetchConversations();
              }, 1000);
            }
            
            return prev;
          });

          // Update conversation list with new message and move to top
          setConversations((prev) => {
            const updatedConversations = prev.map((conv) => {
              if (
                conv.conversationId === conversationId ||
                conv._id === conversationId
              ) {
                const messageContent =
                  typeof message.content === "string"
                    ? message.content
                    : message.content?.text ||
                      message.text ||
                      message.message ||
                      "New message";
                return {
                  ...conv,
                  lastMessage: {
                    content: messageContent,
                    sender: message.sender?.userId || message.senderId,
                    timestamp: message.createdAt || new Date().toISOString(),
                  },
                  updatedAt: message.createdAt || new Date().toISOString(),
                };
              }
              return conv;
            });

            // Sort to move updated conversation to top
            const sortedConversations = updatedConversations.sort((a, b) => {
              const timeA =
                a.lastMessage?.timestamp || a.updatedAt || a.createdAt || 0;
              const timeB =
                b.lastMessage?.timestamp || b.updatedAt || b.createdAt || 0;
              return new Date(timeB) - new Date(timeA);
            });

            // Save updated conversations to localStorage
            try {
              localStorage.setItem(
                "conversations",
                JSON.stringify(sortedConversations)
              );
            } catch (e) {
              console.error("Error saving conversations to localStorage:", e);
            }

            return sortedConversations;
          });
        } catch (err) {
          console.error("Error handling new message:", err);
        }
      };

      const handleMessageRead = (data) => {
        try {
          const { messageId } = data;
          setMessages((prev) =>
            prev.map((msg) =>
              msg._id === messageId ? { ...msg, status: "read" } : msg
            )
          );
        } catch (err) {
          console.error("Error handling message read:", err);
        }
      };

      const handleMessageEdited = (data) => {
        try {
          const { messageId, content, editedAt } = data;
          setMessages((prev) =>
            prev.map((msg) =>
              msg._id === messageId
                ? { ...msg, content, isEdited: true, editedAt }
                : msg
            )
          );
        } catch (err) {
          console.error("Error handling message edited:", err);
        }
      };

      const handleMessageDeleted = (data) => {
        try {
          const { messageId } = data;
          setMessages((prev) => prev.filter((msg) => msg._id !== messageId));
        } catch (err) {
          console.error("Error handling message deleted:", err);
        }
      };

      const handleUserTyping = (data) => {
        try {
          const { userId, name, conversationId } = data;
          if (
            currentConversation &&
            (currentConversation.conversationId === conversationId ||
              currentConversation._id === conversationId)
          ) {
            setTypingUsers(
              (prev) =>
                new Map(prev.set(userId, { name, timestamp: Date.now() }))
            );
          }
        } catch (err) {
          console.error("Error handling user typing:", err);
        }
      };

      const handleUserStoppedTyping = (data) => {
        try {
          const { userId, conversationId } = data;
          if (
            currentConversation &&
            (currentConversation.conversationId === conversationId ||
              currentConversation._id === conversationId)
          ) {
            setTypingUsers((prev) => {
              const newMap = new Map(prev);
              newMap.delete(userId);
              return newMap;
            });
          }
        } catch (err) {
          console.error("Error handling user stopped typing:", err);
        }
      };

      const handleUserOnline = (data) => {
        try {
          console.log('🟢 User came online:', data);
          setOnlineUsers((prev) => {
            const existing = prev.find((user) => user.userId === data.userId);
            if (existing) {
              const updated = prev.map((user) =>
                user.userId === data.userId
                  ? { ...user, isOnline: true, lastSeen: data.lastSeen }
                  : user
              );
              console.log('🟢 Updated existing online user, new list:', updated);
              return updated;
            }
            const newList = [...prev, { ...data, isOnline: true }];
            console.log('🟢 Added new online user, new list:', newList);
            return newList;
          });
        } catch (err) {
          console.error("Error handling user online:", err);
        }
      };

      const handleUserOffline = (data) => {
        try {
          setOnlineUsers((prev) =>
            prev.map((user) =>
              user.userId === data.userId
                ? { ...user, isOnline: false, lastSeen: data.lastSeen }
                : user
            )
          );
        } catch (err) {
          console.error("Error handling user offline:", err);
        }
      };

      const handleError = (error) => {
        console.error("Socket error:", error);
        // Don't set error state here, just log it
      };

      const handleMessageNotification = (data) => {
        try {
          const { conversationId, unreadCount } = data;
          if (conversationId && unreadCount !== undefined) {
            updateUnreadCount(conversationId, unreadCount);
            console.log(
              `Updated unread count for conversation ${conversationId}: ${unreadCount}`
            );
          }
        } catch (err) {
          console.error("Error handling message notification:", err);
        }
      };

      const handleMessageNotificationReceived = (data) => {
        try {
          console.log("🔔 Message notification received in useCommunication:", data);
          const { conversationId, senderName, message } = data;
          
          // Update unread count for the conversation
          if (conversationId) {
            setUnreadCounts((prevCounts) => {
              const currentCount = prevCounts.get(conversationId) || 0;
              const newCount = currentCount + 1;
              const newMap = new Map(prevCounts);
              newMap.set(conversationId, newCount);
              console.log(`📊 Updated unread count for ${conversationId}: ${newCount}`);
              return newMap;
            });
          }
          
          // Update conversation list to show new message preview
          setConversations((prev) => {
            return prev.map((conv) => {
              if (conv.conversationId === conversationId || conv._id === conversationId) {
                return {
                  ...conv,
                  lastMessage: {
                    content: message,
                    sender: data.senderId,
                    timestamp: new Date().toISOString(),
                  },
                  updatedAt: new Date().toISOString(),
                };
              }
              return conv;
            }).sort((a, b) => {
              const timeA = a.lastMessage?.timestamp || a.updatedAt || a.createdAt || 0;
              const timeB = b.lastMessage?.timestamp || b.updatedAt || b.createdAt || 0;
              return new Date(timeB) - new Date(timeA);
            });
          });
        } catch (err) {
          console.error("Error handling message notification received:", err);
        }
      };

      // Join conversations when socket connects
      console.log('🔗 Joining conversations on socket connect');
      socketService.emit("joinConversations");
      
      const handleUnreadCountsUpdate = (data) => {
        try {
          console.log('📊 Received unread counts update:', data);
          const { unreadCounts } = data;
          
          if (unreadCounts && typeof unreadCounts === 'object') {
            setUnreadCounts((prevCounts) => {
              const newMap = new Map(prevCounts);
              Object.entries(unreadCounts).forEach(([conversationId, count]) => {
                const numCount = Number(count);
                if (numCount > 0) {
                  newMap.set(conversationId, numCount);
                } else {
                  newMap.delete(conversationId);
                }
              });
              console.log('📊 Updated unread counts map:', newMap);
              return newMap;
            });
          }
        } catch (err) {
          console.error('Error handling unread counts update:', err);
        }
      };

      const handleNewConversation = (data) => {
        try {
          console.log('🆕 Received new conversation:', data);
          const { conversation } = data;
          
          if (conversation) {
            setConversations((prev) => {
              // Check if conversation already exists
              const exists = prev.some((conv) => 
                conv.conversationId === conversation.conversationId || 
                conv._id === conversation._id
              );
              
              if (!exists) {
                console.log('Adding new conversation to list:', conversation.conversationId);
                return [conversation, ...prev];
              }
              return prev;
            });
          }
        } catch (err) {
          console.error('Error handling new conversation:', err);
        }
      };

      // Add event listeners
      socketService.on("newMessage", (data) => {
        console.log('📨 Received newMessage event:', data);
        handleNewMessage(data);
      });
      socketService.on("conversationsJoined", (data) => {
        console.log('🎉 Successfully joined conversations:', data);
      });
      socketService.on("newConversation", handleNewConversation);
      socketService.on("unreadCountsUpdate", handleUnreadCountsUpdate);
      socketService.on("messageRead", handleMessageRead);
      socketService.on("messageEdited", handleMessageEdited);
      socketService.on("messageDeleted", handleMessageDeleted);
      socketService.on("userTyping", handleUserTyping);
      socketService.on("userStoppedTyping", handleUserStoppedTyping);
      socketService.on("userOnline", handleUserOnline);
      socketService.on("userOffline", handleUserOffline);
      socketService.on("messageNotification", handleMessageNotification);
      socketService.on("messageNotificationReceived", handleMessageNotificationReceived);
      socketService.on("error", handleError);

      // Cleanup
      return () => {
        try {
          clearInterval(onlineUsersInterval);
          socketService.off("conversationsJoined");
          socketService.off("newMessage", handleNewMessage);
          socketService.off("newConversation", handleNewConversation);
          socketService.off("unreadCountsUpdate", handleUnreadCountsUpdate);
          socketService.off("messageRead", handleMessageRead);
          socketService.off("messageEdited", handleMessageEdited);
          socketService.off("messageDeleted", handleMessageDeleted);
          socketService.off("userTyping", handleUserTyping);
          socketService.off("userStoppedTyping", handleUserStoppedTyping);
          socketService.off("userOnline", handleUserOnline);
          socketService.off("userOffline", handleUserOffline);
          socketService.off("messageNotification", handleMessageNotification);
          socketService.off("messageNotificationReceived", handleMessageNotificationReceived);
          socketService.off("error", handleError);
        } catch (err) {
          console.error("Error removing socket listeners:", err);
        }
      };
    } catch (err) {
      console.error("Error setting up socket listeners:", err);
    }
  }, [currentConversation, isConnected, unreadCounts]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Clean up typing indicators after timeout
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingUsers((prev) => {
        const newMap = new Map();
        for (const [userId, data] of prev) {
          if (now - data.timestamp < 3000) {
            // 3 seconds timeout
            newMap.set(userId, data);
          }
        }
        return newMap;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // API Functions
  const fetchConversations = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError(null);

      // Try communication API and unread counts
      let apiConversations = [];
      try {
        const [conversationsResponse, unreadCountsResponse] = await Promise.all(
          [
            communicationAPI.getConversations(page, 20, search),
            communicationAPI.getUnreadCounts().catch((err) => {
              console.error("Unread counts API error:", err);
              return { success: false };
            }),
          ]
        );

        if (conversationsResponse && conversationsResponse.conversations) {
          apiConversations = conversationsResponse.conversations;
        }

        // Update unread counts if available
        if (unreadCountsResponse.success && unreadCountsResponse.unreadCounts) {
          console.log(
            "Fetched unread counts:",
            unreadCountsResponse.unreadCounts
          );
          const newUnreadCounts = new Map();
          Object.entries(unreadCountsResponse.unreadCounts).forEach(([key, value]) => {
            const numValue = Number(value);
            if (numValue > 0) {
              newUnreadCounts.set(key, numValue);
            }
          });
          console.log('Setting unread counts map:', newUnreadCounts);
          setUnreadCounts(newUnreadCounts);
        } else {
          console.log('No unread counts received from API');
        }
      } catch (commError) {
        console.error("Communication API error:", commError);
      }

      // Sort conversations by most recent activity
      apiConversations.sort((a, b) => {
        const timeA =
          a.lastMessage?.timestamp || a.updatedAt || a.createdAt || 0;
        const timeB =
          b.lastMessage?.timestamp || b.updatedAt || b.createdAt || 0;
        return new Date(timeB) - new Date(timeA);
      });

      setConversations(apiConversations);
      return { conversations: apiConversations };
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
      setError("Failed to fetch conversations");
      return { conversations: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId, page = 1) => {
    if (!conversationId) {
      console.error("No conversation ID provided");
      return { messages: [] };
    }

    try {
      setLoading(true);
      setError(null);

      // First check localStorage for existing messages
      const storageKey = `messages_${conversationId}`;
      let existingMessages = [];
      try {
        const storedMessages = localStorage.getItem(storageKey);
        if (storedMessages) {
          existingMessages = JSON.parse(storedMessages);
        }
      } catch (e) {
        console.error("Error loading messages from localStorage:", e);
      }

      // Try to fetch from communication API first
      let newMessages = [];
      try {
        const response = await communicationAPI.getMessages(
          conversationId,
          page,
          50
        );
        if (response && response.messages) {
          newMessages = response.messages;
        }
      } catch (commError) {
        console.error("Communication API error:", commError);

        // Try chat API as fallback
        try {
          newMessages = await getChatMessages(conversationId);
        } catch (chatError) {
          console.error("Chat API error:", chatError);
        }
      }

      if (Array.isArray(newMessages) && newMessages.length > 0) {
        // Merge messages, avoiding duplicates by message ID
        const messageMap = new Map();

        // Add existing messages first
        existingMessages.forEach((msg) => {
          if (msg._id) messageMap.set(msg._id, msg);
        });

        // Add new messages, overwriting existing ones with same ID
        newMessages.forEach((msg) => {
          if (msg._id) messageMap.set(msg._id, msg);
        });

        // Convert back to array and sort by timestamp
        const mergedMessages = Array.from(messageMap.values()).sort((a, b) => {
          const timeA = new Date(a.createdAt || a.sentAt || 0).getTime();
          const timeB = new Date(b.createdAt || b.sentAt || 0).getTime();
          return timeA - timeB;
        });

        // Save to localStorage
        localStorage.setItem(storageKey, JSON.stringify(mergedMessages));

        // Update state
        setMessages(mergedMessages);
        return { messages: mergedMessages };
      } else if (existingMessages.length > 0) {
        // If no new messages but we have stored messages, use those
        setMessages(existingMessages);
        return { messages: existingMessages };
      }

      return { messages: [] };
    } catch (error) {
      console.error("Failed to fetch messages:", error);

      // Try to use localStorage messages if API fails
      try {
        const storageKey = `messages_${conversationId}`;
        const storedMessages = localStorage.getItem(storageKey);
        if (storedMessages) {
          const parsedMessages = JSON.parse(storedMessages);
          setMessages(parsedMessages);
          return { messages: parsedMessages };
        }
      } catch (e) {
        console.error("Error loading messages from localStorage:", e);
      }

      setError("Failed to fetch messages");
      return { messages: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  const sendMessage = useCallback(
    async (conversationId, content, messageType = "text", replyTo = null) => {
      if (!conversationId) {
        console.error("No conversation ID provided");
        return;
      }

      try {
        setError(null);

        // Get current user info
        const currentUser = getCurrentUser();
        const userId = currentUser._id || currentUser.id;
        const userName = currentUser.name || "User";

        if (!userId) {
          throw new Error("User information not available");
        }

        // Ensure content is properly structured
        const messageContent =
          typeof content === "string" ? { text: content } : content;

        // Create storage key
        const storageKey = `messages_${conversationId}`;

        // Optimistic update
        const tempMessage = {
          _id: `temp_${Date.now()}`,
          conversationId,
          content: messageContent,
          text: typeof content === "string" ? content : content.text,
          messageType,
          sender: {
            userId,
            name: userName,
            userType: currentUser.userType || currentUser.role || "staff",
          },
          createdAt: new Date(),
          status: "sending",
          isMine: true,
        };

        // Update UI immediately
        setMessages((prev) => {
          const updatedMessages = [...prev, tempMessage];

          // Save to localStorage
          try {
            localStorage.setItem(storageKey, JSON.stringify(updatedMessages));
          } catch (e) {
            console.error("Error saving to localStorage:", e);
          }

          return updatedMessages;
        });

        // Try to send via communication API first
        let response = null;
        try {
          console.log('📨 Sending message via communication API:', { conversationId, messageContent });
          response = await communicationAPI.sendMessage(
            conversationId,
            messageContent,
            messageType,
            replyTo
          );
          console.log('✅ Communication API response:', response);
        } catch (commError) {
          console.error("Communication API error:", commError);

          // Try chat API as fallback
          try {
            console.log('🚑 Falling back to chat API:', { conversationId, content });
            response = await sendChatMessage(
              conversationId,
              typeof content === "string" ? content : content.text
            );
            console.log('✅ Chat API response:', response);
          } catch (chatError) {
            console.error("Chat API error:", chatError);
            throw new Error("Failed to send message");
          }
        }

        // Replace temp message with real one
        setMessages((prev) => {
          const realMessage = response?.message || response;
          const updatedMessages = prev.map((msg) =>
            msg._id === tempMessage._id
              ? {
                  ...realMessage,
                  _id: realMessage._id || msg._id,
                  status: "sent",
                  sender: {
                    userId,
                    name: userName,
                    userType: currentUser.userType || currentUser.role || "staff",
                  },
                  content: realMessage.content || messageContent,
                  createdAt: realMessage.createdAt || new Date().toISOString(),
                  isMine: true,
                }
              : msg
          );

          // Update localStorage
          try {
            localStorage.setItem(storageKey, JSON.stringify(updatedMessages));
          } catch (e) {
            console.error("Error updating localStorage:", e);
          }

          return updatedMessages;
        });

        return response;
      } catch (error) {
        console.error("Failed to send message:", error);
        setError(error.message || "Failed to send message");

        // Mark temp message as failed on error
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === tempMessage._id
              ? { ...msg, status: "failed" }
              : msg
          )
        );

        throw error;
      }
    },
    []
  );

  const markAsRead = useCallback(
    async (messageId) => {
      try {
        if (isConnected) {
          socketService.markAsRead(messageId);
        }
        await communicationAPI.markAsRead(messageId);
      } catch (error) {
        console.error("Failed to mark message as read:", error);
      }
    },
    [isConnected]
  );

  const editMessage = useCallback(async (messageId, content) => {
    try {
      setError(null);
      const response = await communicationAPI.editMessage(messageId, content);

      // Update message in state
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === messageId
            ? { ...msg, content, isEdited: true, editedAt: new Date() }
            : msg
        )
      );

      return response;
    } catch (error) {
      console.error("Failed to edit message:", error);
      setError(error.message || "Failed to edit message");
      throw error;
    }
  }, []);

  const deleteMessage = useCallback(async (messageId) => {
    try {
      setError(null);
      await communicationAPI.deleteMessage(messageId);

      // Remove message from state
      setMessages((prev) => prev.filter((msg) => msg._id !== messageId));
    } catch (error) {
      console.error("Failed to delete message:", error);
      setError(error.message || "Failed to delete message");
      throw error;
    }
  }, []);

  const createConversation = useCallback(
    async (participants, conversationType = "direct", title, description) => {
      try {
        setError(null);

        // Get current user info
        const currentUser = getCurrentUser();
        if (!currentUser || (!currentUser._id && !currentUser.id)) {
          throw new Error("User information not available");
        }

        // Try communication API first
        let response = null;
        try {
          response = await communicationAPI.createConversation(
            participants,
            conversationType,
            title,
            description
          );
        } catch (commError) {
          console.error("Communication API error:", commError);
          throw commError;
        }

        // Add new conversation to list and refresh
        if (response && response.conversation) {
          setConversations((prev) => [response.conversation, ...prev]);
          
          // Refresh conversations to get the latest state
          setTimeout(() => {
            fetchConversations();
          }, 500);
        }

        return response;
      } catch (error) {
        console.error("Failed to create conversation:", error);
        setError(error.message || "Failed to create conversation");
        throw error;
      }
    },
    [fetchConversations]
  );

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const response = await communicationAPI.getOnlineUsers();
      console.log("🟢 fetchOnlineUsers response:", response);
      const users = response.onlineUsers || [];
      setOnlineUsers(users);
      console.log("🟢 Set online users count:", users.length, users);
      return response;
    } catch (error) {
      console.error("Failed to fetch online users:", error);
    }
  }, []);

  const setTyping = useCallback(
    (conversationId, isTyping) => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      try {
        if (isConnected) {
          socketService.setTyping(conversationId, isTyping);
        }

        if (isTyping) {
          typingTimeoutRef.current = setTimeout(() => {
            if (isConnected) {
              socketService.setTyping(conversationId, false);
            }
          }, 3000);
        }
      } catch (err) {
        console.error("Error setting typing indicator:", err);
      }
    },
    [isConnected]
  );

  const selectConversation = useCallback(
    async (conversation) => {
      if (!conversation) {
        setCurrentConversation(null);
        setMessages([]);
        return;
      }

      setCurrentConversation(conversation);

      try {
        if (isConnected && socketService.socket) {
          socketService.socket.emit("joinConversation", {
            conversationId: conversation.conversationId || conversation._id
          });
        }
      } catch (err) {
        console.error("Error joining conversation:", err);
      }

      await fetchMessages(conversation.conversationId || conversation._id);
    },
    [fetchMessages, isConnected]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Get unread message count for a specific conversation
  const getUnreadCount = useCallback(
    (conversationId) => {
      if (!conversationId) return 0;
      const count = unreadCounts.get(conversationId) || 0;
      console.log(`📊 getUnreadCount for ${conversationId}:`, count, 'from map size:', unreadCounts.size);
      return Number(count) || 0;
    },
    [unreadCounts]
  );

  // Check if a user is online
  const isUserOnline = useCallback(
    (userId) => {
      if (!userId) {
        return false;
      }

      if (!onlineUsers.length) {
        console.log(`🟢 isUserOnline: No online users available (${onlineUsers.length})`);
        return false;
      }

      const targetUserId = userId.toString();
      
      const result = onlineUsers.some((user) => {
        // Handle different user ID formats
        const userOnlineId = user.userId || user._id || user.id;
        const userOnlineIdStr = userOnlineId ? userOnlineId.toString() : "";

        const isMatch = userOnlineIdStr === targetUserId && user.isOnline === true;
        
        if (isMatch) {
          console.log(`🟢 Found online user ${targetUserId}:`, user);
        }

        return isMatch;
      });

      if (result) {
        console.log(`🟢 User ${userId} is ONLINE`);
      }
      
      return result;
    },
    [onlineUsers]
  );

  // Update unread count for a conversation
  const updateUnreadCount = useCallback((conversationId, count) => {
    setUnreadCounts((prev) => {
      const newMap = new Map(prev);
      newMap.set(conversationId, count);
      return newMap;
    });
  }, []);

  // Mark conversation messages as read
  const markConversationAsRead = useCallback(
    async (conversationId) => {
      try {
        // Update local state immediately
        updateUnreadCount(conversationId, 0);

        // Update conversation list to remove unread indicator
        setConversations((prev) =>
          prev.map((conv) =>
            conv.conversationId === conversationId ||
            conv._id === conversationId
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );

        // Send read status via socket if connected
        if (isConnected && socketService.socket) {
          socketService.socket.emit("markConversationAsRead", { conversationId });
        }

        // Also call API to mark as read
        try {
          await communicationAPI.markConversationAsRead(conversationId);
        } catch (apiError) {
          console.error("API error marking conversation as read:", apiError);
        }
      } catch (error) {
        console.error("Error marking conversation as read:", error);
      }
    },
    [isConnected, updateUnreadCount]
  );

  return {
    // State
    conversations,
    currentConversation,
    messages,
    onlineUsers,
    loading,
    error,
    isConnected,
    typingUsers,
    messagesEndRef,
    unreadCounts,

    // Actions
    fetchConversations,
    fetchMessages,
    sendMessage,
    markAsRead,
    editMessage,
    deleteMessage,
    createConversation,
    fetchOnlineUsers,
    setTyping,
    selectConversation,
    clearError,

    // New functions
    getUnreadCount,
    isUserOnline,
    updateUnreadCount,
    markConversationAsRead,

    // Socket service
    socketService,
  };
};
